import i18n from 'helpers/i18n';
import messages from './messages';
import { Tag } from 'types/api/Tag';
import { AxiosResponse } from 'axios';
import { getHoursMinutesSeconds } from '../utils';
import { useEffect, useState } from 'react';
import useCurrentUser from 'hooks/useCurrentUser';
import { useRouteParams } from 'hooks/useRouteParams';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft } from '@teachstonellc/component-library/dist/icons';
import RouteHelpers from 'helpers/routes';
import { USER_ENGAGEMENT_EVENT_VERBS } from 'constants/userEngagementEventVerbs';
import { AspectRatio } from 'components/KalturaVideo/Styled';
import { LearningResource } from 'types/api/LearningResource';
import { getLearningResource } from 'actions/learningResources';
import { getConfiguration, getDomId } from 'pages/Testing/Reliability/utils';
import { VideoPlayerContainer } from 'components/ReliabilityTest/CodingVideos/Styled';
import useUserEngagementEvent from 'hooks/useUserEngagementEvent';
import DimensionTagLabel from 'components/LearningResources/DimensionTagLabel';
import RichTextEditor from 'components/RichTextEditor';
import SupportingAssets from 'components/LearningResources/SupportingAssets';
import MarkAsImportant from 'components/LearningResources/MarkAsImportant';
import { hasLearningResourcesSubscription } from 'components/LearningResources/utils';
import { toastError } from 'components/Toast';

type ParamsType = {
  id: string;
};

// TO DO: move to util when BE json is finalized
const findDimensionTag = (tags?: Tag[]): Tag | undefined => {
  return tags?.find(t => t.group?.name === 'dimension');
};

function DetailPage() {
  const [learningResource, setLearningResource] = useState<LearningResource>();
  const [playbackHasBegun, setPlaybackHasBegun] = useState(false);
  const [playbackHasEnded, setPlaybackHasEnded] = useState(false);
  const { id: learningResourceID } = useRouteParams<ParamsType>();
  const { id: userId } = useCurrentUser();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();

  const isVideo = learningResource?.resource_type !== 'non_video';

  /* upon page view, conditionally transmit the user event */
  useUserEngagementEvent({
    objectId: learningResourceID,
    objectType: 'LearningResourceRecord',
    userId,
    verb: USER_ENGAGEMENT_EVENT_VERBS.FIRST_VIEWS
  });

  const kalturaPlayerID = getDomId(
    `${learningResourceID ? learningResourceID : 1}`
  );

  /* when video playback is initiated, transmit the user event */
  useUserEngagementEvent({
    objectId: learningResourceID,
    objectType: 'LearningResourceRecord',
    userId,
    verb: USER_ENGAGEMENT_EVENT_VERBS.BEGINS_PLAYBACK,
    ready: playbackHasBegun && isVideo
  });

  /* when video is watched to the end, transmit the user event */
  useUserEngagementEvent({
    objectId: learningResourceID,
    objectType: 'LearningResourceRecord',
    userId,
    verb: USER_ENGAGEMENT_EVENT_VERBS.COMPLETES,
    ready: playbackHasEnded && isVideo
  });

  /* load data */
  useEffect(() => {
    if (!learningResourceID) {
      return;
    }

    getLearningResource(parseInt(learningResourceID))
      .then((response: AxiosResponse<LearningResource>) => {
        const data: LearningResource = response.data;
        setLearningResource(data);
      })
      .catch(() => {});
  }, [learningResourceID]);

  // NOTE: Users that haven't an learning resource subscription
  //       should be redirected to the home page
  useEffect(() => {
    if (!hasLearningResourcesSubscription(currentUser)) {
      toastError({
        description: i18n.ft(messages.noSubscription)
      });
      navigate(RouteHelpers.getPath('app'));
    }
  }, [currentUser, navigate]);

  /* set up the video player (only for video resources) */
  useEffect(() => {
    if (!learningResource || learningResource.resource_type === 'non_video') {
      return;
    }

    try {
      /* configure a video player */
      const kalturaPlayer = (window as any).KalturaPlayer?.setup(
        getConfiguration(kalturaPlayerID)
      );

      if (!kalturaPlayer) {
        return;
      }

      /* load the video */
      kalturaPlayer.loadMedia({ entryId: learningResource.video_uri });

      /* listen for the first play event and log it */
      kalturaPlayer.addEventListener(
        kalturaPlayer.Event.Core.FIRST_PLAYING,
        (_e: unknown) => {
          setPlaybackHasBegun(true);
        }
      );

      /* listen for the play back ended event and log it */
      kalturaPlayer.addEventListener(
        kalturaPlayer.Event.Core.PLAYBACK_ENDED,
        (_e: unknown) => {
          setPlaybackHasEnded(true);
        }
      );
    } catch {
      return;
    }

    /* clean up */
    return () => {
      try {
        const kalturaPlayer = (window as any).KalturaPlayer?.getPlayer(
          kalturaPlayerID
        );
        kalturaPlayer?.destroy();
      } catch {
        return;
      }
    };
  }, [kalturaPlayerID, learningResource]);

  if (!learningResource) {
    return <></>;
  }

  return (
    <>
      <div className="my-4 flex font-bold text-blue-500">
        <Link
          to="#"
          onClick={e => {
            e.preventDefault();
            navigate(-1);
          }}
          className="inline-flex items-center gap-1"
        >
          <ChevronLeft />
          {i18n.ft(messages.back)}
        </Link>
      </div>

      <div className={`mt-4 max-w-4xl mb-12 mx-auto`}>
        <h1 className="px-4 text-xl md:text-3xl md:px-0">
          {learningResource.name}
        </h1>
        {isVideo ? (
          <div className="flex justify-center mt-4">
            <AspectRatio />
            <VideoPlayerContainer id={kalturaPlayerID} isTablet={false} />
          </div>
        ) : learningResource.thumbnail_url ? (
          <div className="mt-4">
            <img
              className="w-full rounded-lg object-cover max-h-96"
              src={learningResource.thumbnail_url}
              alt={learningResource.name}
            />
          </div>
        ) : null}
        <div className="mt-4 mx-4 md:mt-0 md:mx-0">
          <div className="bg-white p-4 pb-8">
            <div className="text-base">
              <span className="font-bold text-black">
                {i18n.ft(messages.time)}
              </span>
              <span className="pl-4">
                {getHoursMinutesSeconds(learningResource.estimated_time)}
              </span>
            </div>

            <MarkAsImportant
              learningResourceID={parseInt(learningResourceID)}
              userId={userId}
            />

            <DimensionTagLabel tag={findDimensionTag(learningResource.tags)} />
          </div>
          <div className="bg-white p-4 pb-6 mt-6 md:mt-0">
            {learningResource.content && (
              <div className="text-black">
                <RichTextEditor
                  headings
                  readOnly
                  value={learningResource.content}
                />
              </div>
            )}

            {learningResource.assets ? (
              <SupportingAssets assets={learningResource.assets} />
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default DetailPage;
